/**
 * Edge-marker layout (W1 polish): nav markers whose screen point lies outside the HUD's safe rectangle (off screen,
 * or on screen but under the screen edge or the status, weapons and cruise panels) are pinned onto the safe
 * rectangle along the ray from the screen centre, so the direction stays true. Markers pinned to the same side are
 * spread apart vertically, labels that would run off the right edge flip to the left of their icon, and every
 * label then goes through the H2 declutter (`declutterLabels`) so no two plates overlap; the losers keep the icon.
 * Allocation-free per frame (D-014): the caller fills the scratch arrays in place.
 */
import type { ReachHudConfig } from './config'
import { createLabelDeclutter, declutterLabels, LABEL_FULL, reserveLabelRect, resetLabelDeclutter, type LabelDeclutter } from './labels'

type EdgeRules = ReachHudConfig['edgeMarkers']
type LabelRules = ReachHudConfig['labels']

/** Pinned side of a marker. */
export const EDGE_NONE = 0
export const EDGE_LEFT = 1
export const EDGE_RIGHT = 2
export const EDGE_TOP = 3
export const EDGE_BOTTOM = 4

/** Inputs (filled by the caller each frame) and outputs of the layout. */
export interface EdgeMarkerLayout {
  count: number
  /** Inputs: the projected point (on screen) and the edge angle (0 = up, clockwise, radians). */
  readonly px: Float32Array
  readonly py: Float32Array
  readonly angle: Float32Array
  readonly onScreen: Uint8Array
  readonly distance: Float64Array
  /** Declutter kind ('station' / 'poi' …) and label text (name only; the distance is budgeted by distChars). */
  readonly kind: string[]
  readonly text: string[]
  /** Outputs: the marker position, its pinned side (EDGE_*), label flipped to the left (1), label text shown (1). */
  readonly x: Float32Array
  readonly y: Float32Array
  readonly side: Uint8Array
  readonly flip: Uint8Array
  readonly full: Uint8Array
  /** Scratch: the plate anchor handed to the declutter, and the side-spread order. */
  readonly anchorX: Float32Array
  readonly order: Int32Array
  readonly declutter: LabelDeclutter
  /** Reserved rectangles (x0, y0, x1, y1) that labels must avoid, e.g. the combat target's edge arrow. */
  readonly reserved: Float32Array
  reservedCount: number
}

/** Reserved rectangles the layout keeps room for (the combat target arrow and the contract nav arrow). */
const MAX_RESERVED = 2

export function createEdgeMarkerLayout(max: number): EdgeMarkerLayout {
  const kind: string[] = []
  const text: string[] = []
  for (let i = 0; i < max; i++) {
    kind.push('station')
    text.push('')
  }
  return {
    count: 0,
    px: new Float32Array(max),
    py: new Float32Array(max),
    angle: new Float32Array(max),
    onScreen: new Uint8Array(max),
    distance: new Float64Array(max),
    kind,
    text,
    x: new Float32Array(max),
    y: new Float32Array(max),
    side: new Uint8Array(max),
    flip: new Uint8Array(max),
    full: new Uint8Array(max),
    anchorX: new Float32Array(max),
    order: new Int32Array(max),
    declutter: createLabelDeclutter(max),
    reserved: new Float32Array(MAX_RESERVED * 4),
    reservedCount: 0,
  }
}

/** Clears the reserved rectangles; call once per frame before `reserveEdgeRect`. */
export function resetEdgeReserved(l: EdgeMarkerLayout): void {
  l.reservedCount = 0
}

/** Keeps labels out of a rectangle around (x, y): an edge arrow and its distance readout. Ignored when full. */
export function reserveEdgeArrow(l: EdgeMarkerLayout, x: number, y: number, rules: EdgeRules): void {
  if (l.reservedCount >= MAX_RESERVED) return
  const a = rules.arrowRectPx
  const k = l.reservedCount * 4
  l.reserved[k] = x - a.left
  l.reserved[k + 1] = y - a.up
  l.reserved[k + 2] = x + a.right
  l.reserved[k + 3] = y + a.down
  l.reservedCount++
}

/** Width of a label plate (CSS px), estimated from the text length like the H2 declutter. */
function plateWidth(chars: number, labels: LabelRules): number {
  const c = labels.declutter
  return (chars + c.distChars) * c.charPx + c.platePadPx
}

/**
 * Lays out `l.count` markers for a `width` × `height` viewport. Markers inside the safe rectangle stay where they
 * project; the rest are pinned to it along the centre ray. Allocation-free.
 */
export function layoutEdgeMarkers(l: EdgeMarkerLayout, width: number, height: number, rules: EdgeRules, labels: LabelRules): void {
  const n = l.count < l.x.length ? l.count : l.x.length
  const inset = rules.insetPx
  const cx = width * 0.5
  const cy = height * 0.5
  // The safe rectangle, kept around the centre even on a tiny viewport.
  const x0 = inset.left < cx - 1 ? inset.left : cx - 1
  const x1 = width - inset.right > cx + 1 ? width - inset.right : cx + 1
  const y0 = inset.top < cy - 1 ? inset.top : cy - 1
  const y1 = height - inset.bottom > cy + 1 ? height - inset.bottom : cy + 1

  // 1. Pin: anything outside the safe rectangle moves onto it along the ray from the centre.
  for (let i = 0; i < n; i++) {
    const inside = l.onScreen[i] === 1 && l.px[i] >= x0 && l.px[i] <= x1 && l.py[i] >= y0 && l.py[i] <= y1
    if (inside) {
      l.x[i] = l.px[i]
      l.y[i] = l.py[i]
      l.side[i] = EDGE_NONE
      continue
    }
    const a = l.angle[i]
    const dx = Math.sin(a)
    const dy = -Math.cos(a)
    const tX = dx > 1e-9 ? (x1 - cx) / dx : dx < -1e-9 ? (x0 - cx) / dx : Infinity
    const tY = dy > 1e-9 ? (y1 - cy) / dy : dy < -1e-9 ? (y0 - cy) / dy : Infinity
    if (tX <= tY) {
      l.x[i] = dx > 0 ? x1 : x0
      l.y[i] = cy + dy * tX
      l.side[i] = dx > 0 ? EDGE_RIGHT : EDGE_LEFT
    } else {
      l.x[i] = cx + dx * tY
      l.y[i] = dy > 0 ? y1 : y0
      l.side[i] = dy > 0 ? EDGE_BOTTOM : EDGE_TOP
    }
  }

  // 2. Spread the markers pinned to the left and right sides so their rows never stack.
  spreadSide(l, n, EDGE_LEFT, y0, y1, rules.spacingPx)
  spreadSide(l, n, EDGE_RIGHT, y0, y1, rules.spacingPx)

  // 3. Flip labels that would run past the safe rectangle's right edge; the declutter sees the flipped plate.
  const c = labels.declutter
  for (let i = 0; i < n; i++) {
    const w = plateWidth(l.text[i].length, labels)
    const flip = l.x[i] + c.offsetXPx + w > x1 + inset.right * 0.5
    l.flip[i] = flip ? 1 : 0
    l.anchorX[i] = flip ? l.x[i] - 2 * c.offsetXPx - w : l.x[i]
  }

  // 4. Declutter every plate (H2): reserved arrows first, then priority order; losers keep the icon.
  const d = l.declutter
  resetLabelDeclutter(d)
  for (let k = 0; k < l.reservedCount; k++) {
    const r = k * 4
    reserveLabelRect(d, l.reserved[r], l.reserved[r + 1], l.reserved[r + 2], l.reserved[r + 3])
  }
  declutterLabels(d, n, l.anchorX, l.y, l.distance, l.kind, l.text, n, cx, cy, false, labels)
  for (let i = 0; i < n; i++) l.full[i] = d.show[i] === LABEL_FULL ? 1 : 0
}

/** Sorts the markers pinned to `side` by y and pushes them apart to `spacing`, kept within [y0, y1]. */
function spreadSide(l: EdgeMarkerLayout, n: number, side: number, y0: number, y1: number, spacing: number): void {
  const o = l.order
  let m = 0
  for (let i = 0; i < n; i++) {
    if (l.side[i] !== side) continue
    let j = m
    while (j > 0 && l.y[o[j - 1]] > l.y[i]) {
      o[j] = o[j - 1]
      j--
    }
    o[j] = i
    m++
  }
  if (m < 2) return
  // Push down from the top, then back up from the bottom limit if the stack ran past it.
  for (let k = 1; k < m; k++) {
    const min = l.y[o[k - 1]] + spacing
    if (l.y[o[k]] < min) l.y[o[k]] = min
  }
  if (l.y[o[m - 1]] > y1) l.y[o[m - 1]] = y1
  for (let k = m - 2; k >= 0; k--) {
    const max = l.y[o[k + 1]] - spacing
    if (l.y[o[k]] > max) l.y[o[k]] = max
  }
  if (l.y[o[0]] < y0) l.y[o[0]] = y0
}
